/**
 * OnboardingModal - Post-signup optional questionnaire
 *
 * Shown after first signup to collect optional information:
 * - Use cases (multi-select)
 * - Industry
 * - Company size
 *
 * All fields are skippable - user can dismiss and continue.
 *
 * @see DESIGN_SYSTEM.md - Uses dark theme, Apple-style design
 */

import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// Use case options (multi-select)
const USE_CASE_OPTIONS = [
  { value: 'team_meetings', label: 'Team Meetings', icon: '👥' },
  { value: 'client_calls', label: 'Client Calls', icon: '💼' },
  { value: 'one_on_ones', label: '1:1 Meetings', icon: '🤝' },
  { value: 'interviews', label: 'Interviews', icon: '📋' },
  { value: 'webinars', label: 'Webinars', icon: '🎤' },
  { value: 'education', label: 'Education/Training', icon: '📚' },
  { value: 'personal', label: 'Personal Calls', icon: '👋' },
  { value: 'other', label: 'Other', icon: '✨' }
];

// Industry options
const INDUSTRY_OPTIONS = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'legal', label: 'Legal' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'nonprofit', label: 'Non-profit' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' }
];

// Company size options (ranges only)
const COMPANY_SIZE_OPTIONS = [
  { value: 'just_me', label: 'Just me' },
  { value: '2-5', label: '2-5' },
  { value: '6-10', label: '6-10' },
  { value: '11-25', label: '11-25' },
  { value: '26-50', label: '26-50' },
  { value: '51-100', label: '51-100' },
  { value: '100+', label: '100+' }
];

export default function OnboardingModal({ isOpen, onClose, onComplete }) {
  const { user, userProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedUseCases, setSelectedUseCases] = useState([]);
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [saving, setSaving] = useState(false);

  // Check if name is missing (Apple edge case)
  const nameMissing = userProfile?.nameMissing || (!userProfile?.firstName && !userProfile?.lastName);
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');

  if (!isOpen) return null;

  const toggleUseCase = (value) => {
    setSelectedUseCases(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const handleSkip = async () => {
    // Mark onboarding as skipped/completed
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          onboardingCompleted: true,
          onboardingSkipped: true,
          onboardingCompletedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onClose?.();
    onComplete?.();
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const updates = {
          onboardingCompleted: true,
          onboardingCompletedAt: serverTimestamp()
        };

        // Only add fields that have values
        if (selectedUseCases.length > 0) {
          updates.useCases = selectedUseCases;
        }
        if (industry) {
          updates.industry = industry;
        }
        if (companySize) {
          updates.companySize = companySize;
        }
        if (displayName && nameMissing) {
          updates.displayName = displayName;
          updates.nameMissing = false;
          // Try to parse into first/last name
          const parts = displayName.trim().split(/\s+/);
          if (parts.length >= 1) {
            updates.firstName = parts[0];
            updates.lastName = parts.slice(1).join(' ') || '';
            updates.fullName = displayName.trim();
          }
        }

        await updateDoc(userRef, updates);
      }
      onClose?.();
      onComplete?.();
    } catch (error) {
      console.error('Error saving onboarding data:', error);
    } finally {
      setSaving(false);
    }
  };

  const totalSteps = nameMissing ? 4 : 3;

  const getStepTitle = () => {
    if (step === 1 && nameMissing) return "What should we call you?";
    if (step === 1 && !nameMissing) return "How will you use QuickMeet?";
    if (step === 2 && nameMissing) return "How will you use QuickMeet?";
    if (step === 2 && !nameMissing) return "What industry are you in?";
    if (step === 3 && nameMissing) return "What industry are you in?";
    if (step === 3 && !nameMissing) return "How big is your team?";
    if (step === 4) return "How big is your team?";
    return "";
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-[#1c1c1e] border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {getStepTitle()}
            </h2>
            <button
              onClick={handleSkip}
              className="text-sm text-white/50 hover:text-white transition-colors"
            >
              Skip
            </button>
          </div>

          {/* Progress bar */}
          <div className="flex gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full flex-1 transition-colors ${
                  i + 1 <= step ? 'bg-[#06c]' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Step 1: Name (only if missing) */}
          {step === 1 && nameMissing && (
            <div className="space-y-4">
              <p className="text-sm text-white/60">
                We didn't get your name from Apple. How should we display your name?
              </p>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors"
                autoFocus
              />
            </div>
          )}

          {/* Use Cases Step */}
          {((step === 1 && !nameMissing) || (step === 2 && nameMissing)) && (
            <div className="space-y-3">
              <p className="text-sm text-white/60 mb-4">
                Select all that apply (optional)
              </p>
              <div className="grid grid-cols-2 gap-2">
                {USE_CASE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => toggleUseCase(option.value)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl text-left transition-all ${
                      selectedUseCases.includes(option.value)
                        ? 'bg-[#06c]/20 border border-[#06c] text-white'
                        : 'bg-white/5 border border-white/10 text-white/80 hover:border-white/20'
                    }`}
                  >
                    <span className="text-lg">{option.icon}</span>
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Industry Step */}
          {((step === 2 && !nameMissing) || (step === 3 && nameMissing)) && (
            <div className="space-y-3">
              <p className="text-sm text-white/60 mb-4">
                This helps us tailor your experience (optional)
              </p>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white' stroke-opacity='0.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="" className="bg-[#1c1c1e]">Select your industry</option>
                {INDUSTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#1c1c1e]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Company Size Step */}
          {((step === 3 && !nameMissing) || (step === 4 && nameMissing)) && (
            <div className="space-y-3">
              <p className="text-sm text-white/60 mb-4">
                How many people are on your team? (optional)
              </p>
              <div className="grid grid-cols-4 gap-2">
                {COMPANY_SIZE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setCompanySize(option.value)}
                    className={`px-3 py-3 rounded-xl text-center transition-all ${
                      companySize === option.value
                        ? 'bg-[#06c] text-white'
                        : 'bg-white/5 border border-white/10 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex-1 px-4 py-3.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
            >
              Back
            </button>
          )}
          {step < totalSteps ? (
            <button
              onClick={() => setStep(step + 1)}
              className="flex-1 px-4 py-3.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={saving}
              className="flex-1 px-4 py-3.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                'Get Started'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
